import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseFormComponent } from '@classes/extends';
import { FormValidator } from '@composants/validators';
import { FicheclientService as FicheClientService, LaiterieFiche, Organisme } from '@estel-numerique/galaclick-api';
import { TraductionCoreService, ValidatorPatternService } from '@estel-numerique/galaclick-core';
import { MilliersPipe } from '@services/index';
import { Constantes } from '@services/util/constantes';
import { lastValueFrom } from 'rxjs';
import { DialogGalaComponent } from '../../../../composants/dialog-gala/dialog-gala.component';
import { SelectGala } from '../../../../composants/select/select.component';
import { InputGala } from '../../../../composants/inputs/input.component';
import { DateGalaComponent } from '../../../../composants/date-gala/date-gala.component';
import { MatButton } from '@angular/material/button';

export interface FicheClientLaiteriesComponentData {
    idClient: number;
    listLaiteriesClient: LaiterieFiche[];
    laiterie?: LaiterieFiche
}
@Component({
    templateUrl: 'ficheclient-laiteries.component.html',
    imports: [DialogGalaComponent, FormsModule, ReactiveFormsModule, SelectGala, InputGala, DateGalaComponent, MatButton]
})
export class FicheClientLaiteriesComponent extends BaseFormComponent implements OnInit {
    public traductionCore = inject(TraductionCoreService);
    public validPattern = inject(ValidatorPatternService);
    private formBuilder = inject(UntypedFormBuilder);
    private wsFicheClient = inject(FicheClientService);
    public milliersPipe = inject(MilliersPipe);
    private ref = inject(ChangeDetectorRef);
    private dialogRef = inject<MatDialogRef<FicheClientLaiteriesComponent>>(MatDialogRef);
    public data = inject<FicheClientLaiteriesComponentData>(MAT_DIALOG_DATA);

    public codeModule: string = Constantes.CodeOption.CFICHECLIENT; // Code module/option testé pour vérifier l'accès à cette fonction (si null = OK)

    @Input() public titre: string;
    @Output() saved = new EventEmitter<LaiterieFiche>();

    // Traductions
    protected lgTrad: Record<string, string> = {};

    public laiterie: LaiterieFiche = {};
    public listeLaiteries: Organisme[];
    public listeLaiteriesClient: LaiterieFiche[];
    public idClient: number;
    public isEdition: boolean;
    public isLaiterie: boolean = null;

    constructor() {
        super();
        this.dialogRef.updateSize('600px');
        this.lgTrad = {
            AFFLAIT: this.translate.instant('APPFICHECLIENT.AFFLAIT'),
            LAITERIE: this.translate.instant('APPFICHECLIENT.LAITERIE'),
            VANNUEL: this.translate.instant('APPFICHECLIENT.VANNUEL'),
            VALANNUELLE: this.translate.instant('APPFICHECLIENT.VALANNUELLE'),
            REFANNUELLE: this.translate.instant('APPFICHECLIENT.REFANNUELLE'),
            ALLPROVISOIRE: this.translate.instant('APPFICHECLIENT.ALLPROVISOIRE'),
            DROITAPROD: this.translate.instant('APPFICHECLIENT.DROITAPROD'),
            DEBUT: this.translate.instant('DEBUT'),
            FIN: this.translate.instant('FIN'),
            BEGINNINGDATE: this.translate.instant('BEGINNINGDATE'),
            ENDDATE: this.translate.instant('ENDDATE'),
            ENR: this.translate.instant('ENR'),
            RET: this.translate.instant('RET'),
        };
        this.formData = this.formBuilder.group({
            ORGANISME: [null, [Validators.required]],
            VOLUME: [null],
            VALORISATION: [null],
            REFANNUELLE: [null],
            ALLOCATION: [null],
            DROIT: [null],
            DDEBUT: [null, [Validators.required, FormValidator.validDate]],
            DFIN: [null, [FormValidator.validDate]],
        }, { validators: [this.util.date.compareDate('DDEBUT', 'DFIN')] });
    }

    ngOnInit() {
        super.ngOnInit();
        this.show(this.data.idClient, this.data.listLaiteriesClient, this.data.laiterie).catch(e => this.util.fErreur(e));
    }

    // =========================================================================
    // CHARGEMENT CONTEXTE
    // =========================================================================

    private async show(idClient: number, listLaiteriesClient: LaiterieFiche[], laiterie?: LaiterieFiche) {
        try {
            this.util.showLoader();

            this.idClient = idClient;
            this.listeLaiteriesClient = listLaiteriesClient;
            this.listeLaiteries = await lastValueFrom(this.wsCacheLow.getLaiteries());
            const transformationExists = this.listeLaiteries.some(l => l.IDORGA === -1);

            if (!transformationExists) {
                this.listeLaiteries.push({
                    IDORGA: -1,
                    NOM: "Transformation"
                });
            } else {
                const transformationEntry = this.listeLaiteries.find(l => l.IDORGA === -1);
                if (transformationEntry && transformationEntry.NOM !== "Transformation") {
                    transformationEntry.NOM = "Transformation";
                }
            }

            if (laiterie) {
                this.laiterie = laiterie;
                this.isEdition = true;
            } else {
                this.isEdition = false;
            }

            this.formData.markAsPristine();
            this.formData.markAsUntouched();

            this.chargerForm();
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    async enregistrerLaiteries() {
        this.util.showLoader();
        try {
            this.validationFormulaire(this.formData);
            if (this.formData.valid) {
                this.getResultatFromForm();
                if (!this.checkTransfoActive()) {
                    if (this.isEdition) {
                        this.laiterie = await this.wsFicheClient.putLaiterie(this.idClient, this.laiterie).toPromise();
                    } else {
                        this.laiterie = await this.wsFicheClient.postLaiterie(this.idClient, this.laiterie).toPromise();
                    }
                    this.saved.emit(this.laiterie);
                    this.hideModal();
                }
            }
        } catch (e) {
            this.util.fErreur(e);
        } finally {
            this.util.hideLoader();
        }
    }

    public checkTransfoActive() {
        if (!this.isEdition) {
            if (this.laiterie.ORGANISME.IDORGA > -1) {
                return false;
            }

            let hasTransfoActif = false;
            this.listeLaiteriesClient.forEach((l: LaiterieFiche) => {
                if (l.ORGANISME.IDORGA == -1) {
                    hasTransfoActif = true;
                }
            });
            if (hasTransfoActif) {
                this.util.fErreur(this.translate.instant('APPFICHECLIENT.ERRORTRANSFO'));
            }
            return hasTransfoActif;
        } else {
            return false;
        }
    }

    public getResultatFromForm() {
        Object.assign(this.laiterie, this.formData.value);
        this.setInformations();
    }

    public chargerForm() {
        if (this.isEdition) {
            this.ref.detectChanges();

            this.laiterie.ORGANISME = this.listeLaiteries.find((o: Organisme) => {
                return this.laiterie.ORGANISME.IDORGA == o.IDORGA;
            });
            this.chargerLaiterie(this.laiterie.ORGANISME);

            this.formData.reset(this.laiterie);
            this.formData.markAsUntouched();
            this.calculerDroit();
        }
    }

    public chargerLaiterie(orga: Organisme) {
        this.isLaiterie = (orga.IDORGA > -1) ? true : false;
    }

    public calculerDroit() {
        const res: number = Number(this.formData.get('REFANNUELLE').value) +
            Number(this.formData.get('ALLOCATION').value);
        this.formData.get('DROIT').setValue(res);
    }

    public setInformations() {
        if (this.laiterie.ORGANISME.IDORGA > -1) {
            if (this.laiterie.REFANNUELLE) {
                this.laiterie.INFORMATIONS = "Référence annuelle : " + this.milliersPipe.transform(this.laiterie.REFANNUELLE) + " L";
            } else {
                this.laiterie.INFORMATIONS = '';
            }
        } else {
            this.laiterie.INFORMATIONS = "Volume annuel : " + this.milliersPipe.transform(this.laiterie.VOLUME) + " L, Valorisation annuelle : "
                + this.milliersPipe.transform(this.laiterie.VALORISATION) + " €/1 000L";
        }
    }

    askHideModal() {
        if (!this.isEdition && !this.formData.untouched) {
            this.util.fConfirm(this.translate.instant('QUITNOSAVE'), () => {
                this.hideModal();
            }, this.translate.instant('MODIFDONE'));
        } else {
            this.hideModal();
        }
    }

    hideModal() {
        this.resetModalValue();
        this.dialogRef.close();
    }

    resetModalValue() {
        this.laiterie = {};
        this.isLaiterie = null;
        this.formData.reset();
        this.formData.markAsPristine();
        this.formData.markAsUntouched();
    }

}
